// Local B2C pricing dataset — snapshot of migration 013 seed data.
// Used only when the Supabase pricing tables/RPC are unreachable; the backend
// is authoritative. Every commercial value here mirrors the b2c_* seed rows
// and remains editable from /admin/pricing — nothing is consulted by the
// public UI unless the backend is down.
import { doorFamilies } from '../data/configurator.js';

const TIER_RANGES = [[1, 2], [3, 7], [8, 21], [22, 35], [36, null]];

const tiers = [];
const seedTiers = (familyId, variant, prices) => {
  TIER_RANGES.forEach(([min, max], i) => {
    tiers.push({
      id: `seed-${familyId}-${variant}-${min}`,
      family_id: familyId, variant,
      qty_min: min, qty_max: max,
      price: prices[i] ?? null,
      data_status: prices[i] != null ? 'verified' : 'placeholder'
    });
  });
};

// Seed values from the supplied price list. COTTAGE intentionally unpriced.
seedTiers('60_IN', 'default', [364, 322, 308, 280, 280]);
seedTiers('60_OUT', 'default', [341, 301, 288, 262, 262]);
seedTiers('60_OUT_PLUS', 'default', [423, 374, 358, 325, 325]);
seedTiers('18_DEG', 'default', [273, 242, 231, 210, 210]);
seedTiers('MERANTI', 'no_glass', [124, 109, 105, 95, 95]);
seedTiers('MERANTI', 'with_glass', [143, 127, 121, 110, 110]);

const factors = [
  { id: 'f-global', scope: 'global', scope_key: null, operation: 'MULTIPLIER', value: 1.2, label: 'Global B2C factor', enabled: true, sort: 0 },
  { id: 'f-double', scope: 'option', scope_key: 'design_double', operation: 'MULTIPLIER', value: 2.5, label: 'Double door factor (×2.5)', enabled: true, sort: 40 },
  ...['60_OUT', '60_IN', '60_OUT_PLUS', 'MERANTI', '18_DEG', 'COTTAGE'].map((id) => ({
    id: `f-${id}`, scope: 'family', scope_key: id, operation: 'MULTIPLIER',
    value: 1.0, label: `${id} family factor`, enabled: true, sort: 10
  }))
];

const rule = (id, familyId, kind, target, meta, qtyMin, qtyMax, op, value, unit, label) => ({
  id, family_id: familyId, rule_kind: kind, target, meta,
  qty_min: qtyMin, qty_max: qtyMax, op, value, unit, label, data_status: 'verified'
});

const rules = [
  rule('r-l2150', null, 'option', 'lock_fix2150', null, 1, null, 'add', 50, 'door', 'ASSA 2150 3-point lock'),
  rule('r-mpl410', null, 'option', 'lock_mpl410', null, 1, null, 'add', 95, 'door', 'MPL-410 lock'),
  rule('r-8561', null, 'option', 'lock_assa8561', null, 1, null, 'add', 64, 'door', 'ASSA 8561 lock'),
  rule('r-8765', null, 'option', 'lock_8765', null, 1, null, 'add', 10, 'door', 'Type 8765 lock'),
  rule('r-t-oak18', '18_DEG', 'option', 'threshold_oak', null, 1, null, 'add', 15, 'door', '18° oak threshold'),
  rule('r-t-acc18', '18_DEG', 'option', 'threshold_accoya', null, 1, null, 'add', 17, 'door', '18° Accoya threshold'),
  rule('r-t-acc60', '60_OUT', 'option', 'threshold_accoya', null, 1, null, 'add', 10, 'door', 'Smartline Accoya threshold'),
  rule('r-col12', null, 'option', 'colour_nonstandard', null, 1, 2, 'add', 60, 'door', 'Non-standard colour 1–2'),
  rule('r-col39', null, 'option', 'colour_nonstandard', null, 3, 9, 'add', 40, 'door', 'Non-standard colour 3–9'),
  rule('r-h2115', null, 'dimension', 'height_special', { heights: [2115, 2118] }, null, null, 'add', 15, 'door', 'Height 2115/2118 mm'),
  rule('r-nonstd', null, 'special_size', 'nonstandard_size', null, null, null, 'pct', 30, 'door', 'Special dimensions +30%'),
  rule('r-gl016a', null, 'option', 'glass_gl016', null, 1, 2, 'add', 95, 'door', 'GL016 1–2'),
  rule('r-gl016b', null, 'option', 'glass_gl016', null, 3, 5, 'add', 80, 'door', 'GL016 3–5'),
  rule('r-gl016c', null, 'option', 'glass_gl016', null, 6, null, 'add', 75, 'door', 'GL016 6+'),
  rule('r-gl019a', null, 'option', 'glass_gl019d', null, 1, 2, 'add', 60, 'door', 'GL019D 1–2'),
  rule('r-gl019b', null, 'option', 'glass_gl019d', null, 3, 5, 'add', 45, 'door', 'GL019D 3–5'),
  rule('r-gl019c', null, 'option', 'glass_gl019d', null, 6, null, 'add', 40, 'door', 'GL019D 6+'),
  rule('r-gl003a', null, 'option', 'glass_gl003', null, 1, 2, 'add', 70, 'door', 'GL003 1–2'),
  rule('r-gl003b', null, 'option', 'glass_gl003', null, 3, 5, 'add', 55, 'door', 'GL003 3–5'),
  rule('r-gl003c', null, 'option', 'glass_gl003', null, 6, null, 'add', 50, 'door', 'GL003 6+'),
  rule('r-x-offh', null, 'option', 'extra_offset_hinge', null, 1, null, 'add', 5, 'hinge', 'Offset hinge'),
  rule('r-x-3248', null, 'option', 'extra_3248_hinge', null, 1, null, 'add', 4, 'hinge', 'Extra 3248 hinge'),
  rule('r-x-wtrn', null, 'option', 'extra_waternose', null, 1, null, 'add', 6, 'door', 'Smartline waternose'),
  rule('r-x-kick', null, 'option', 'extra_kickplate', null, 1, null, 'add', 40, 'door', 'Kick plate'),
  rule('r-x-2col', null, 'option', 'extra_two_colours', null, 1, null, 'add', 50, 'door', 'Two colours'),
  rule('r-x-line', null, 'option', 'extra_lines_hdf', null, 1, null, 'add', 10, 'door', 'Lines in HDF'),
  rule('r-x-pack', null, 'option', 'extra_packaging', null, 1, null, 'add', 5, 'door', 'Cardboard packaging'),
  rule('r-x-frp', null, 'option', 'extra_frame_rear_paint', null, 1, null, 'add', 10, 'door', 'Frame rear-side painting')
];

// Sidelight dataset — mirrors migration 014 seeds. All commercial values are
// NULL until Hestia configures them in /admin/pricing; geometry deductions are
// documented approximations pending production confirmation.
const sidelight = {
  // Sidelights are supported by every door family.
  families: { '60_OUT': true, '60_IN': true, '60_OUT_PLUS': true, MERANTI: true, '18_DEG': true, COTTAGE: true },
  base: doorFamilies.map((f) => ({
    id: `seed-sl-base-${f.id}`, family_id: f.id, variant: 'default',
    base_price: null, enabled: true, data_status: 'placeholder'
  })),
  glass: [
    { id: 'seed-sl-g-clear', glass_type: 'CLEAR', name: 'Clear glass', name_et: 'Selge klaas', price_per_m2: null, enabled: true, families: null, sort: 10 },
    { id: 'seed-sl-g-frosted', glass_type: 'FROSTED', name: 'Frosted glass', name_et: 'Matt klaas', price_per_m2: null, enabled: true, families: null, sort: 20 },
    { id: 'seed-sl-g-lam', glass_type: 'LAMINATED', name: 'Laminated glass', name_et: 'Lamineeritud klaas', price_per_m2: null, enabled: false, families: null, sort: 30 },
    { id: 'seed-sl-g-temp', glass_type: 'TEMPERED', name: 'Tempered glass', name_et: 'Karastatud klaas', price_per_m2: null, enabled: false, families: null, sort: 40 },
    { id: 'seed-sl-g-safe', glass_type: 'SAFETY_GLASS', name: 'Safety glass', name_et: 'Turvaklaas', price_per_m2: null, enabled: false, families: null, sort: 50 },
    { id: 'seed-sl-g-cust', glass_type: 'CUSTOM', name: 'Custom glass', name_et: 'Erilahendus', price_per_m2: null, enabled: false, families: null, sort: 60 }
  ],
  dividers: [
    { id: 'seed-sl-div-h', divider_type: 'horizontal', name: 'Horizontal timber divider', fixed_price: null, price_per_lm: null, enabled: true },
    { id: 'seed-sl-div-v', divider_type: 'vertical', name: 'Vertical timber divider', fixed_price: null, price_per_lm: null, enabled: true }
  ],
  widths: [300, 400, 500, 600].map((w, i) => ({ id: `seed-sl-w-${w}`, width_mm: w, enabled: true, sort: (i + 1) * 10 })),
  settings: {
    min_width_mm: 200, max_width_mm: 1200, min_height_mm: 600, max_height_mm: 2400,
    deduction_left_mm: 40, deduction_right_mm: 40, deduction_top_mm: 40,
    deduction_bottom_mm: 40, deduction_divider_mm: 40,
    custom_width_mode: 'manual', special_size_op: 'manual', special_size_value: null,
    height_default: 'door'
  }
};

export const pricingSeed = {
  families: doorFamilies,
  priceList: { id: 'seed-pl', code: 'B2C_ESTONIA_2026', version: 1, currency: 'EUR', rounding_mode: 'NEAREST', rounding_step: 5 },
  tiers,
  factors,
  rules,
  standardOptions: ['colour_white', 'colour_black', 'colour_anthracite'],
  overrides: [],
  sidelight
};
